import React, { useState, useEffect, useCallback } from 'react';
import {Link2, Smartphone, Apple, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Loader2, Save, Copy, Shield} from 'lucide-react';import {Button} from '@/components/ui/button';import {useAdmin} from '@/contexts/AdminContext';import {useAdminTheme} from '@/contexts/AdminThemeContext';import {toast} from 'sonner';import AdminLayout from './AdminLayout';
import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';

export default function AdminDeepLinksPage() {
  const { admin } = useAdmin();
  const { colors, isDark } = useAdminTheme();
  const isSuperAdmin = admin?.role === 'super_admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/deep-links/status`, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error('Failed to load deep link status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl" data-testid="admin-deep-links-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Deep Link Validation</h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
              Verify iOS Universal Links and Android App Links configuration
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchStatus(true)}
            disabled={refreshing}
            data-testid="refresh-deep-links-btn"
            style={{ borderColor: colors.cardBorder, color: colors.text }}
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {data && (
          <>
            <ConfigSection config={data.config} isSuperAdmin={isSuperAdmin} colors={colors} isDark={isDark} onUpdated={() => fetchStatus(true)} />
            <EndpointCard
              title="Apple Universal Links"
              icon={<Apple className="h-5 w-5" />}
              status={data.apple}
              isPlaceholder={data.config.apple_placeholder}
              placeholderField="Apple Team ID"
              endpoint="/.well-known/apple-app-site-association"
              colors={colors}
              isDark={isDark}
            />
            <EndpointCard
              title="Android App Links"
              icon={<Smartphone className="h-5 w-5" />}
              status={data.android}
              isPlaceholder={data.config.android_placeholder}
              placeholderField="SHA-256 Fingerprint"
              endpoint="/.well-known/assetlinks.json"
              colors={colors}
              isDark={isDark}
            />
            <PathsSection paths={data.paths} colors={colors} isDark={isDark} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function ConfigSection({ config, isSuperAdmin, colors, isDark, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    apple_team_id: config.apple_team_id,
    android_sha256: config.android_sha256,
    app_bundle_id: config.app_bundle_id,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/deep-links/update-config`, form, { withCredentials: true });
      toast.success('Configuration updated');
      setEditing(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    { label: 'Bundle ID', key: 'app_bundle_id', value: config.app_bundle_id },
    { label: 'Apple Team ID', key: 'apple_team_id', value: config.apple_team_id, placeholder: config.apple_placeholder },
    { label: 'Android SHA-256', key: 'android_sha256', value: config.android_sha256, placeholder: config.android_placeholder },
  ];

  return (
    <div className="rounded-lg border p-5" style={{ borderColor: colors.cardBorder, background: colors.cardBg }} data-testid="deep-link-config-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4" style={{ color: colors.accent }} />
          <h2 className="font-semibold" style={{ color: colors.text }}>Configuration</h2>
        </div>
        {isSuperAdmin && !editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="edit-config-btn" style={{ color: colors.accent }}>
            Edit
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {rows.map(({ label, key, value, placeholder }) => (
          <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <span className="text-sm font-medium w-36 shrink-0" style={{ color: colors.textMuted }}>{label}</span>
            {editing ? (
              <input
                className="flex-1 text-sm px-3 py-1.5 rounded border outline-none"
                style={{ borderColor: colors.inputBorder, background: colors.input, color: colors.text }}
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                data-testid={`config-input-${key}`}
              />
            ) : (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <code className="text-sm truncate" style={{ color: placeholder ? (isDark ? '#f59e0b' : '#d97706') : colors.text }}>
                  {value}
                </code>
                {placeholder && (
                  <span className="text-xs px-1.5 py-0.5 rounded shrink-0" style={{ background: isDark ? '#78350f' : '#fef3c7', color: isDark ? '#fbbf24' : '#92400e' }}>
                    placeholder
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <div className="flex gap-2 mt-4 pt-3" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-config-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)} style={{ color: colors.textMuted }}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

function EndpointCard({ title, icon, status, isPlaceholder, placeholderField, endpoint, colors, isDark }) {
  const [expanded, setExpanded] = useState(false);
  const isOk = status.reachable && !status.error;
  const isJson = status.content_type?.includes('application/json');

  const copyResponse = () => {
    navigator.clipboard.writeText(JSON.stringify(status.response, null, 2));
    toast.success('Copied to clipboard');
  };

  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: colors.cardBorder, background: colors.cardBg }} data-testid={`endpoint-card-${title.includes('Apple') ? 'apple' : 'android'}`}>
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)' }}>
              {icon}
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: colors.text }}>{title}</h3>
              <code className="text-xs" style={{ color: colors.textMuted }}>{endpoint}</code>
            </div>
          </div>
          <StatusBadge ok={isOk} isDark={isDark} />
        </div>

        <div className="mt-4 space-y-2">
          <CheckRow ok={isOk} label="Endpoint reachable" isDark={isDark} colors={colors} />
          <CheckRow ok={isJson} label={`Content-Type: ${status.content_type || 'N/A'}`} isDark={isDark} colors={colors} />
          <CheckRow ok={!isPlaceholder} label={isPlaceholder ? `${placeholderField} is a placeholder` : `${placeholderField} configured`} warn={isPlaceholder} isDark={isDark} colors={colors} />
          {status.error && (
            <div className="text-xs mt-2 px-3 py-2 rounded" style={{ background: isDark ? '#450a0a' : '#fef2f2', color: isDark ? '#fca5a5' : '#dc2626' }}>
              {status.error}
            </div>
          )}
        </div>
      </div>

      {status.response && (
        <div style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
          <button
            className="w-full px-5 py-2.5 text-left text-sm flex items-center justify-between hover:opacity-80 transition-opacity"
            style={{ color: colors.textMuted }}
            onClick={() => setExpanded(!expanded)}
            data-testid={`toggle-response-${title.includes('Apple') ? 'apple' : 'android'}`}
          >
            <span>{expanded ? 'Hide' : 'View'} Response</span>
            <div className="flex items-center gap-2">
              <button onClick={e => { e.stopPropagation(); copyResponse(); }} className="hover:opacity-70" data-testid={`copy-response-${title.includes('Apple') ? 'apple' : 'android'}`}>
                <Copy className="h-3.5 w-3.5" />
              </button>
              <span className="text-xs">{expanded ? '-' : '+'}</span>
            </div>
          </button>
          {expanded && (
            <pre className="px-5 pb-4 text-xs overflow-x-auto" style={{ color: colors.textMuted }}>
              {JSON.stringify(status.response, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function PathsSection({ paths, colors, isDark }) {
  return (
    <div className="rounded-lg border p-5" style={{ borderColor: colors.cardBorder, background: colors.cardBg }} data-testid="deep-link-paths-section">
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="h-4 w-4" style={{ color: colors.accent }} />
        <h2 className="font-semibold" style={{ color: colors.text }}>Registered Paths</h2>
      </div>
      <p className="text-xs mb-3" style={{ color: colors.textMuted }}>
        These URL paths will open in the native app when installed
      </p>
      <div className="flex flex-wrap gap-2">
        {paths.map(path => (
          <code
            key={path}
            className="text-xs px-2.5 py-1 rounded"
            style={{ background: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.06)', color: isDark ? '#a5b4fc' : '#4f46e5' }}
          >
            {path}
          </code>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ ok, isDark }) {
  return ok ? (
    <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full" style={{ background: isDark ? '#052e16' : '#dcfce7', color: isDark ? '#4ade80' : '#16a34a' }} data-testid="status-badge-ok">
      <CheckCircle2 className="h-3 w-3" /> Live
    </span>
  ) : (
    <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full" style={{ background: isDark ? '#450a0a' : '#fef2f2', color: isDark ? '#f87171' : '#dc2626' }} data-testid="status-badge-error">
      <XCircle className="h-3 w-3" /> Error
    </span>
  );
}

function CheckRow({ ok, label, warn, isDark, colors }) {
  const Icon = warn ? AlertTriangle : ok ? CheckCircle2 : XCircle;
  const iconColor = warn
    ? (isDark ? '#fbbf24' : '#d97706')
    : ok
      ? (isDark ? '#4ade80' : '#16a34a')
      : (isDark ? '#f87171' : '#dc2626');

  return (
    <div className="flex items-center gap-2 text-sm" style={{ color: colors.text }}>
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: iconColor }} />
      <span>{label}</span>
    </div>
  );
}
